<template>
  <div id="product-mobile-footer-bar" ref="footerBarRef" class="transition-transform transition-opacity w-full fixed bottom-0 left-0">
    <mobile-footer-price-bar :action-text="actionBtnText" :is-submittable="!isSoldOut" @submit="handleActionBtnClick">
      <p class="price font-500 text-base flex items-center gap-1">
        <span v-if="dateTimeText">{{ dateTimeText }}</span>
        <span v-else class="text-gray-60">{{ $t('product_select_time_place_holder') }}</span>
      </p>
    </mobile-footer-price-bar>
  </div>
</template>

<script setup lang="ts">
import { differenceInDays, parseISO } from 'date-fns'

import MobileFooterPriceBar from '@/components/common/mobile-footer-price-bar.vue'

import type { UiProductInfo, UiProductSelectedData } from '@/types/product'

const { t } = useI18n()
const { format } = await useI18nDateFns()

const FAQ_SECTION_ID = 'product-detail-section-faq'

const props = defineProps<{
  product: UiProductInfo
  bookingItem: UiProductSelectedData[]
}>()

const isSoldOut = inject<ComputedRef<boolean>>('isSoldOut')

const footerBarRef = ref<Nullable<HTMLElement>>(null)

const actionBtnText = computed(() => (isSoldOut?.value ? t('all_inventory_status_sold_out') : t('all_reserve')))

const dateTimeText = computed(() => {
  const startItem = props.bookingItem?.at?.(0)
  if (!startItem?.dateTime) return ''

  const startDate = parseISO(startItem.dateTime)
  let result = `${format(startDate, 'yyyy/MM/dd HH:mm')}`

  const endItem = props.bookingItem?.at?.(-1)
  if (!endItem?.dateTime || startItem === endItem) return result

  const endDate = parseISO(endItem.dateTime)
  result += ` ~ ${format(endDate, 'yyyy/MM/dd HH:mm')}`

  const days = differenceInDays(endDate, startDate)
  result += ` (${days}天)`

  return result
})

onMounted(() => {
  const sectionFaq = document.getElementById(FAQ_SECTION_ID)
  if (!sectionFaq || !footerBarRef.value) return
  useIntersectionObserver(
    sectionFaq,
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          footerBarRef.value?.classList.add('opacity-0')
        } else {
          footerBarRef.value?.classList.remove('opacity-0')
        }
      })
    },
    { threshold: 0.01 }
  )
})

const handleActionBtnClick = () => {
  scrollToId('product-detail-section-reservation', false)
}
</script>

<style lang="scss" scoped></style>
